// src/contexts/CompanyContext.js
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useUser } from './UserContext'
import useFetch from 'hooks/useFetch'
import { isAdminRole } from 'utils/roles'

const CompanyContext = createContext()

const STORAGE_KEY = 'selectedCompany'
// Marca de que el admin ya ha elegido explícitamente (incluido "todas las empresas").
// Sin ella asumimos primer arranque y aplicamos la empresa por defecto.
const CHOSEN_KEY  = 'selectedCompanyChosen'
// Usuario al que pertenece la selección guardada
const OWNER_KEY   = 'selectedCompanyOwner'

// Nombre de la empresa preseleccionada para admins en el primer arranque.
export const DEFAULT_COMPANY_NAME =
  process.env.REACT_APP_DEFAULT_COMPANY_NAME || 'Gestinet'

const readStoredCompany = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null
  } catch {
    return null
  }
}

const readHasChosen = () => {
  try {
    // Compatibilidad: si ya había una empresa guardada, cuenta como elección previa.
    return localStorage.getItem(CHOSEN_KEY) === '1' || !!localStorage.getItem(STORAGE_KEY)
  } catch {
    return false
  }
}

/**
 * Empresa activa de la sesión.
 *
 * Para un admin la lista de empresas y la preselección se resuelven AQUÍ, no en
 * el selector del navbar: las páginas necesitan saber qué empresa toca antes de
 * pedir datos. Mientras no se sabe, companyReady es false y las páginas esperan,
 * para no lanzar una consulta a "todas las empresas" que luego hay que repetir.
 */
export function CompanyProvider({ children }) {
  const { user } = useUser()
  const isAdmin = isAdminRole(user?.role)

  // Empresa elegida por un admin en el selector ({ id, name }); null = todas las empresas
  const [adminCompany, setAdminCompany] = useState(readStoredCompany)
  const [hasChosen,    setHasChosen]    = useState(readHasChosen)
  const [companies,    setCompanies]    = useState([])
  // true cuando ya se sabe qué empresa usar (o se ha intentado y no se ha podido)
  const [resolved,     setResolved]     = useState(false)

  const [, fetchAction] = useFetch()
  const loadedFor = useRef(null)

  const persist = (company) => {
    if (company) localStorage.setItem(STORAGE_KEY, JSON.stringify(company))
    else localStorage.removeItem(STORAGE_KEY)
  }

  const setSelectedCompany = (company) => {
    if (!isAdmin) return
    setAdminCompany(company)
    setHasChosen(true)
    localStorage.setItem(CHOSEN_KEY, '1')
    persist(company)
  }

  // Recarga la lista (p. ej. tras crear/editar/borrar empresas en Admin).
  // Si la empresa seleccionada ya no existe, se vuelve a "todas".
  const refreshCompanies = async () => {
    if (!isAdmin) return
    const result = await fetchAction({ url: 'companies/', method: 'GET' })
    if (!Array.isArray(result)) return
    setCompanies(result)
    if (adminCompany && !result.some(c => c.id === adminCompany.id)) {
      setAdminCompany(null)
      persist(null)
    }
  }

  // Carga de empresas + preselección de fábrica. Solo para admins y solo una vez
  // por usuario: loadedFor evita repetirla en cada render o remontaje.
  useEffect(() => {
    if (!user) { setResolved(false); return }

    // Si la selección guardada es de otro usuario, se descarta: si no, un admin
    // heredaría la empresa (o la lista) del que entró antes en este navegador.
    if (user.id && localStorage.getItem(OWNER_KEY) !== String(user.id)) {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(CHOSEN_KEY)
      localStorage.setItem(OWNER_KEY, String(user.id))
      setAdminCompany(null)
      setHasChosen(false)
      setCompanies([])
      setResolved(false)
    }

    if (!isAdmin) {
      // Los demás roles usan siempre su propia empresa: nada que resolver.
      setResolved(true)
      return
    }

    if (loadedFor.current === user.id) return
    loadedFor.current = user.id

    let cancelled = false

    ;(async () => {
      try {
        const result = await fetchAction({ url: 'companies/', method: 'GET' })
        if (cancelled) return

        const list = Array.isArray(result) ? result : []
        setCompanies(list)

        // Preselección: solo si este admin nunca ha elegido nada.
        // No marca CHOSEN_KEY, para que siga siendo un default y no una elección suya.
        if (!readHasChosen() && list.length > 0) {
          const target = list.find(
            c => (c.name || '').trim().toLowerCase() === DEFAULT_COMPANY_NAME.toLowerCase()
          )
          if (target) {
            const company = { id: target.id, name: target.name }
            setAdminCompany(company)
            persist(company)
          }
        }
      } catch {
        // Sin lista no hay preselección posible; se sigue con lo que hubiera guardado.
      } finally {
        // Pase lo que pase, se desbloquea a las páginas: mejor cargar con la
        // empresa guardada (o todas) que dejar la aplicación en blanco.
        if (!cancelled) setResolved(true)
      }
    })()

    // Si se desmonta antes de terminar (p. ej. StrictMode monta los efectos dos
    // veces en desarrollo), se libera la marca para que el siguiente montaje
    // vuelva a cargar; si no, la respuesta descartada dejaría la lista vacía.
    return () => {
      cancelled = true
      loadedFor.current = null
    }
  }, [user?.id, isAdmin]) // eslint-disable-line

  // Admin: la que ha elegido. Resto de roles: siempre su propia empresa.
  const selected = isAdmin
    ? adminCompany
    : (user?.company_id ? { id: user.company_id, name: user.company_name } : null)

  return (
    <CompanyContext.Provider value={{
      selectedCompanyId:   selected?.id ?? null,
      selectedCompanyName: selected?.name ?? null,
      setSelectedCompany,
      companies,
      refreshCompanies,
      hasChosenCompany:    hasChosen,
      canSwitchCompany:    isAdmin,
      // Las páginas no deben pedir datos hasta que esto sea true.
      companyReady:        resolved,
    }}>
      {children}
    </CompanyContext.Provider>
  )
}

export function useCompany() {
  return useContext(CompanyContext)
}

CompanyProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
